// Shared connection state for the Ganforge shell. One probe, many
// consumers: the status strip, the dashboard hero, the settings test
// button all read the same signals, so the app never disagrees with
// itself about whether the index host is reachable.

import { createSignal } from "solid-js";
import {
  configured,
  forgeErrorMessage,
  loadRepos,
  toolsList,
  type RepoRef,
  type ToolDef,
} from "../../lib/ganforge";

export type ConnState = "unconfigured" | "no-repos" | "checking" | "ok" | "error";

const [state, setState] = createSignal<ConnState>("unconfigured");
const [tools, setTools] = createSignal<ToolDef[]>([]);
const [error, setError] = createSignal<string>("");
const [probedAgainst, setProbedAgainst] = createSignal<RepoRef | null>(null);
const [latencyMs, setLatencyMs] = createSignal<number | null>(null);

export const connState = state;
export const connTools = tools;
export const connError = error;
export const connRepo = probedAgainst;
export const connLatency = latencyMs;

/** Probe the host: tools/list against `ref` (default: first tracked repo).
 *  Cheap, read-only, and it exercises auth — a 401 shows up here first. */
export async function probeConnection(ref?: RepoRef): Promise<ConnState> {
  if (!configured()) {
    setState("unconfigured");
    return "unconfigured";
  }
  const target = ref ?? loadRepos()[0];
  if (!target) {
    setState("no-repos");
    return "no-repos";
  }
  setState("checking");
  setProbedAgainst(target);
  const started = performance.now();
  try {
    const list = await toolsList(target);
    setLatencyMs(Math.round(performance.now() - started));
    setTools(list);
    setError("");
    setState("ok");
    return "ok";
  } catch (e) {
    setLatencyMs(null);
    setTools([]);
    setError(forgeErrorMessage(e));
    setState("error");
    return "error";
  }
}

let probedOnce = false;

/** Fire the probe once per page load (the shell calls this on mount). */
export function probeOnce(): void {
  if (probedOnce) return;
  probedOnce = true;
  void probeConnection();
}
